import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Survey from '../components/survey/Survey'
import apiServices from '../libs/services'
import { LoadingSpinner } from '../components/atoms'
import { useAuth } from '../hooks/useAuth'
import { PRE_SURVEY_ID_SLUG } from './PreSurveyPage'

export const POST_SURVEY_ID_SLUG = 'iPOST_SURVEY'

function PostSurveyPage (): ReactElement {
  const navigate = useNavigate()
  const { userProfile, refreshUserProfile } = useAuth()
  const [surveyId, setSurveyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchSurveyMetadata = async (): Promise<void> => {
      try {
        const [postRes, preRes] = await Promise.all([
          apiServices.getSurveyByUserGeneratedId(POST_SURVEY_ID_SLUG),
          apiServices.getSurveyByUserGeneratedId(PRE_SURVEY_ID_SLUG)
        ])
        const postId = postRes.data?.id as string | undefined
        const preId = preRes.data?.id as string | undefined

        if (postId === undefined || postId === null) {
          setError('Survey ID not found in response.')
          return
        }

        const completedIds = userProfile?.user_profile?.completed_survey_ids ?? []
        const preCompleted = preId != null && completedIds.includes(preId)
        const postCompleted = completedIds.includes(postId)
        const certDownloaded = userProfile?.user_profile?.certificate_downloaded === true

        // Redirect if conditions not met
        if (!preCompleted || !certDownloaded || postCompleted) {
          navigate('/user-dashboard', { replace: true })
          return
        }

        setSurveyId(postId)
      } catch (err: any) {
        console.error('Failed to fetch survey metadata.', err)
        setError('Failed to load survey metadata.')
      } finally {
        setLoading(false)
      }
    }

    void fetchSurveyMetadata()
  }, [navigate, userProfile])

  const handleFinish = async (): Promise<void> => {
    await refreshUserProfile()
    navigate('/user-dashboard', { replace: true })
  }

  if (loading) {
    return (
      <div className='bg-dopage-black min-h-screen flex items-center justify-center'>
        <LoadingSpinner />
      </div>
    )
  }

  if (error !== null || surveyId === null) {
    return (
      <div className='bg-dopage-black min-h-screen flex items-center justify-center text-red-500 font-primary'>
        {error ?? 'An unexpected error occurred.'}
      </div>
    )
  }

  return <Survey surveyId={surveyId} onFinish={handleFinish} />
}

export default PostSurveyPage
